'use client'

import { useEffect } from 'react'
import { createPortal } from 'react-dom'
import Link from 'next/link'
import { X } from 'lucide-react'
import type { Tenant, NavLink } from '@sifly/shared-types'
import { formatPhone } from '@sifly/shared-utils'
import { hasNavHref } from './navHref'

export type MobileNavProps = {
  links: NavLink[]
  tenant: Tenant
  isOpen: boolean
  onClose: () => void
  /** Optional analytics hook. Fired on every nav-link click (top-level + nested
   * children) in addition to closing the drawer. shared-ui stays analytics-
   * agnostic — the storefront passes a callback routing into its consent-gated
   * `track('nav_click', meta)` path. */
  onLinkClick?: (meta: { label: string; href: string }) => void
}

export function MobileNav({ links, tenant, isOpen, onClose, onLinkClick }: MobileNavProps) {
  // Close on escape key
  useEffect(() => {
    if (!isOpen) return
    function handleKeyDown(e: KeyboardEvent) {
      if (e.key === 'Escape') onClose()
    }
    document.addEventListener('keydown', handleKeyDown)
    // Prevent body scroll when open
    document.body.style.overflow = 'hidden'
    return () => {
      document.removeEventListener('keydown', handleKeyDown)
      document.body.style.overflow = ''
    }
  }, [isOpen, onClose])

  if (!isOpen || typeof document === 'undefined') return null

  // Portal to <body> so the fixed-position overlay anchors to the viewport,
  // not the header. The header (classic_left variant) sets `backdrop-blur-sm`,
  // and `backdrop-filter` makes that element a containing block for fixed
  // descendants — without the portal the drawer + backdrop only cover the
  // header strip and the page bleeds through underneath. (Same portal pattern
  // as CountryPicker.)
  return createPortal(
    <>
      {/* Backdrop */}
      <div
        className="fixed inset-0 bg-black/30 z-40"
        onClick={onClose}
        aria-hidden="true"
      />

      {/* Panel */}
      <div
        className="fixed inset-y-0 left-0 w-full max-w-sm bg-[var(--color-bg-primary)] z-50 flex flex-col animate-slideInLeft"
        role="dialog"
        aria-modal="true"
        aria-label="Navigation menu"
      >
        {/* Header */}
        <div className="flex items-center justify-between px-6 py-4 border-b border-[var(--color-border)]">
          <span
            className="font-semibold text-[var(--color-text-primary)]"
            style={{ fontFamily: 'var(--font-heading)' }}
          >
            {tenant.name}
          </span>
          <button
            onClick={onClose}
            className="text-[var(--color-text-secondary)] hover:text-[var(--color-text-primary)] transition-colors"
            aria-label="Close menu"
          >
            <X size={20} />
          </button>
        </div>

        {/* Links — top-level entries with nested children indented underneath */}
        <nav className="flex-1 px-6 py-6 space-y-1 overflow-y-auto">
          {links.map((link) => (
            <div key={link.id} className="border-b border-[var(--color-border)]/50">
              {hasNavHref(link) ? (
                <Link
                  href={link.href}
                  onClick={() => { onLinkClick?.({ label: link.label, href: link.href }); onClose() }}
                  className="block py-3 text-lg text-[var(--color-text-primary)] hover:text-[var(--color-cta-readable)] transition-colors"
                  style={{ fontFamily: 'var(--font-body)' }}
                >
                  {link.label}
                </Link>
              ) : (
                // A group heading with no link of its own; its children follow.
                <p
                  className="py-3 text-lg text-[var(--color-text-primary)]"
                  style={{ fontFamily: 'var(--font-body)' }}
                >
                  {link.label}
                </p>
              )}
              {link.children && link.children.length > 0 && (
                <ul className="pb-2 space-y-1">
                  {link.children.map((child) => (
                    <li key={child.id}>
                      {hasNavHref(child) ? (
                        <Link
                          href={child.href}
                          onClick={() => { onLinkClick?.({ label: child.label, href: child.href }); onClose() }}
                          className="block pl-4 py-2 text-base text-[var(--color-text-secondary)] hover:text-[var(--color-cta-readable)] transition-colors"
                          style={{ fontFamily: 'var(--font-body)' }}
                        >
                          {child.label}
                        </Link>
                      ) : (
                        <span
                          className="block pl-4 py-2 text-base text-[var(--color-text-secondary)]"
                          style={{ fontFamily: 'var(--font-body)' }}
                        >
                          {child.label}
                        </span>
                      )}
                    </li>
                  ))}
                </ul>
              )}
            </div>
          ))}
        </nav>

        {/* Contact info — respects tenants.show_public_* (migration 146).
            Instrumented like the nav links above: on a phone this block is
            where the number is actually legible (the header bar has room for
            an icon only below `lg`), so leaving it untracked would have made
            the mobile half of contact intent invisible. Labels are stable keys
            rather than the rendered value, so a dealer changing their number
            does not fragment the series. */}
        <div className="px-6 py-4 border-t border-[var(--color-border)]">
          {tenant.show_public_phone !== false && tenant.phone && (
            <a
              href={`tel:${tenant.phone}`}
              className="block text-sm text-[var(--color-text-secondary)] hover:text-[var(--color-text-primary)] mb-1"
              onClick={() => onLinkClick?.({ label: 'mobile_nav:phone', href: `tel:${tenant.phone}` })}
            >
              {formatPhone(tenant.phone)}
            </a>
          )}
          {tenant.show_public_email !== false && tenant.public_email && (
            <a
              href={`mailto:${tenant.public_email}`}
              className="block text-sm text-[var(--color-text-secondary)] hover:text-[var(--color-text-primary)]"
              onClick={() => onLinkClick?.({ label: 'mobile_nav:email', href: `mailto:${tenant.public_email}` })}
            >
              {tenant.public_email}
            </a>
          )}
        </div>
      </div>
    </>,
    document.body,
  )
}
